/**
 * Patient programmes (Phase 20F): palliative care, hospice election, PPECC attendance.
 * GET /patientmr/{patientId}/programs, PUT /patientmr/{patientId}/programs/{program}.
 */

export type ProgramName = 'palliative' | 'hospice' | 'ppecc';

export const CODE_STATUSES = ['FULL_CODE', 'DNR', 'OOH_DNR', 'LIMITED_INTERVENTIONS'] as const;
export type CodeStatus = typeof CODE_STATUSES[number];
export const CODE_STATUS_LABELS: Record<CodeStatus, string> = {
  FULL_CODE: 'Full code', DNR: 'DNR', OOH_DNR: 'Out-of-hospital DNR', LIMITED_INTERVENTIONS: 'Limited interventions',
};

export const PLACES_OF_CARE = ['HOME', 'HOSPITAL', 'HOSPICE_FACILITY', 'UNDECIDED'] as const;
export type PlaceOfCare = typeof PLACES_OF_CARE[number];
export const PLACE_OF_CARE_LABELS: Record<PlaceOfCare, string> = {
  HOME: 'Home', HOSPITAL: 'Hospital', HOSPICE_FACILITY: 'Hospice facility', UNDECIDED: 'Undecided',
};

export const WEEKDAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'] as const;
export type Weekday = typeof WEEKDAYS[number];

export interface PalliativeProgram {
  active: boolean;
  startDate: string | null;
  endDate?: string | null;
  enrollingClinician: string | null;
  reason: string | null;
  codeStatus?: CodeStatus | null;
  goalsOfCare?: { conversationDate?: string | null; participants?: string | null; patientFamilyGoals?: string | null; preferences?: string | null; decisions?: string | null };
  comfortPlan?: { symptomPlan?: string | null; comfortMeasures?: string | null; emergencyPlan?: string | null; preferredPlaceOfCare?: PlaceOfCare | null };
  bereavement?: { followUpDate?: string | null; notes?: string | null };
}

export interface HospiceElection {
  electionDate: string | null;
  hospiceName: string | null;
  hospiceNpi: string | null;
  revocationDate?: string | null;
  notes?: string | null;
}

export interface PpeccAttendance {
  centerName: string | null;
  centerNpi: string | null;
  startDate: string | null;
  endDate?: string | null;
  attendanceDays: Weekday[];
  hoursPerDay?: number | null;
}

export interface ProgramRecord<T> {
  program: ProgramName;
  version: number;
  data: T;
  updatedAt?: string;
  updatedBy?: string;
}

export interface PatientPrograms {
  palliative: ProgramRecord<PalliativeProgram> | null;
  hospice: ProgramRecord<HospiceElection> | null;
  ppecc: ProgramRecord<PpeccAttendance> | null;
}
